import React, { useState } from 'react';
import {
  AlertTriangle,
  Info,
  XCircle,
  Eye,
  CheckCircle2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

export default function DFMReport({
  part_id = '',
  findings = [],
  findings_count,
  summary,
  onFocusFeatures
}) {
  const [expanded, setExpanded] = useState(true);
  const [toastMessage, setToastMessage] = useState('');

  const count = findings_count ?? findings.length;

  const handleFocus = (features) => {
    const featStr = Array.isArray(features) ? features.join(', ') : features;
    if (onFocusFeatures) {
      onFocusFeatures(Array.isArray(features) ? features : [features]);
    }
    setToastMessage(`CAD Viewport: Focused on [${featStr}]`);
    setTimeout(() => setToastMessage(''), 2500);
  };

  return (
    <div className="border border-slate-200 bg-white rounded-xl shadow-xs overflow-hidden my-3 text-slate-800 animate-in fade-in duration-200">
      {/* Header */}
      <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-6 h-6 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs">
            DFM
          </div>
          <div>
            <h4 className="font-bold text-xs text-slate-900 tracking-tight flex items-center gap-2">
              <span>Design for Manufacturability Audit</span>
              {part_id && (
                <span className="font-mono text-[10px] text-slate-400 font-normal">
                  {part_id}
                </span>
              )}
            </h4>
            {summary && <p className="text-[11px] text-slate-500 mt-0.5">{summary}</p>}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-200/80 text-slate-700 font-mono">
            {count} {count === 1 ? 'Rule Item' : 'Rule Items'}
          </span>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 transition-colors cursor-pointer"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <div className="bg-blue-600 text-white text-[11px] font-medium px-3 py-1.5 flex items-center justify-between animate-in fade-in">
          <span>{toastMessage}</span>
          <span className="text-[10px] opacity-80">3D Highlight</span>
        </div>
      )}

      {/* Body: Findings List */}
      {expanded && (
        <div className="p-3 space-y-2.5">
          {findings.length === 0 ? (
            <div className="p-4 text-center text-xs text-slate-400 italic">
              No DFM violations detected.
            </div>
          ) : (
            findings.map((f, idx) => {
            const sev = (f.severity || 'INFO').toUpperCase();
            const isFail = sev === 'FAIL' || sev === 'ERROR';
            const isWarn = sev === 'WARN' || sev === 'WARNING';
            const features = f.features || f.face_ids || (f.feature_id ? [f.feature_id] : []);

            return (
              <div
                key={idx}
                className={`p-3 rounded-lg border text-xs flex items-start gap-2.5 transition-colors ${
                  isFail
                    ? 'bg-red-50/70 border-red-200 text-red-950'
                    : isWarn
                    ? 'bg-amber-50/70 border-amber-200 text-amber-950'
                    : 'bg-blue-50/60 border-blue-200 text-blue-950'
                }`}
              >
                {/* Icon */}
                <div className="shrink-0 mt-0.5">
                  {isFail && <XCircle className="w-4 h-4 text-red-600" />}
                  {isWarn && <AlertTriangle className="w-4 h-4 text-amber-600" />}
                  {!isFail && !isWarn && <Info className="w-4 h-4 text-blue-600" />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`font-mono text-[9px] font-bold px-1.5 py-0.2 rounded uppercase ${
                          isFail
                            ? 'bg-red-200/80 text-red-800'
                            : isWarn
                            ? 'bg-amber-200/80 text-amber-800'
                            : 'bg-blue-200/80 text-blue-800'
                        }`}
                      >
                        {sev}
                      </span>
                      <span className="font-semibold text-xs text-slate-900">
                        {f.check || f.rule || 'Geometry Rule'}
                      </span>
                    </div>

                    {features.length > 0 && (
                      <button
                        type="button"
                        onClick={() => handleFocus(features)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer ml-auto"
                      >
                        <Eye className="w-3 h-3" />
                        <span>Focus {Array.isArray(features) ? features.join(', ') : features}</span>
                      </button>
                    )}
                  </div>

                  <p className="mt-1 text-slate-700 text-[11px] leading-relaxed">
                    {f.message || f.description}
                  </p>

                  {(f.fix || f.recommendation) && (
                    <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-slate-600 bg-white/70 rounded p-1.5 border border-slate-200/60">
                      <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold text-slate-700">Recommended fix: </span>
                        <span>{f.fix || f.recommendation}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          }))}
        </div>
      )}
    </div>
  );
}
